// src/cockpit4/components/ContentPredictPanel.tsx
// 内容效果预测与爆款反向工程面板（MVP）
// 闭环：爆款样本 → 特征反推 → 预测 → 发布 → 实际数据回填 → 校准 → 第二天改善
// 铁律一：样本必须真实录入，无数据时显示「暂无」，绝不造假
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Database, FlaskConical, TrendingUp, RotateCcw, Plus, Trash2,
  BarChart3, Hash, Clock, Type, Image as ImageIcon,
  Layers, CheckCircle2, Target,
} from "lucide-react";
import {
  contentPerformanceEngine,
  type ViralSample,
  type PredictPlatform,
  type ContentForm,
  type PredictInput,
  type ContentPrediction,
  type ReverseEngineeredFormula,
} from "../../services/contentPerformanceEngine";

const PLATFORMS: Array<{ id: PredictPlatform; label: string }> = [
  { id: "wechat_official", label: "公众号" },
  { id: "xiaohongshu", label: "小红书" },
  { id: "douyin_enterprise", label: "抖音企业号" },
  { id: "douyin_personal", label: "抖音个人号" },
  { id: "zhihu", label: "知乎" },
  { id: "bilibili", label: "B站" },
];
const FORMS: Array<{ id: ContentForm; label: string }> = [
  { id: "article", label: "长文" }, { id: "image_text", label: "图文" },
  { id: "short_video", label: "短视频" }, { id: "long_video", label: "长视频" }, { id: "audio", label: "音频" },
];
const PLATFORM_COLOR: Record<PredictPlatform, string> = {
  wechat_official: "#34D399", xiaohongshu: "#F472B6", douyin_enterprise: "#38BDF8",
  douyin_personal: "#60A5FA", zhihu: "#A78BFA", bilibili: "#FB7185",
};
function fmt(n: number): string {
  if (n >= 10000) return `${(n / 10000).toFixed(1)}万`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}
const now = () => new Date().toTimeString().slice(0, 5);
function defaultSampleForm(): Omit<ViralSample, "id" | "recordedAt"> {
  return {
    platform: "xiaohongshu", title: "", coverFeatures: "", scriptStructure: "", tags: [],
    form: "image_text", views: 0, likes: 0, comments: 0, favorites: 0, followersGain: 0,
    publishTime: now(),
  };
}
function defaultPredictInput(): PredictInput {
  return {
    platform: "xiaohongshu", title: "", coverFeatures: "", scriptStructure: "", tags: [],
    form: "image_text", publishTime: now(),
  };
}

export default function ContentPredictPanel() {
  const [tab, setTab] = useState<"samples" | "reverse" | "predict" | "review">("samples");
  const [samples, setSamples] = useState<ViralSample[]>(() => contentPerformanceEngine.listSamples());
  const [sampleForm, setSampleForm] = useState(defaultSampleForm());
  const [tagInput, setTagInput] = useState("");
  const [msg, setMsg] = useState<{ type: "ok" | "error"; text: string } | null>(null);
  const refresh = useCallback(() => setSamples(contentPerformanceEngine.listSamples()), []);

  // 启动时尝试从 Vault 回填样本（换设备/清缓存后仍能取回实爆款样本，不新增假数据）
  useEffect(() => {
    void contentPerformanceEngine.hydrateSamplesFromVault().then((ok) => {
      if (ok) refresh();
    });
  }, [refresh]);
  const notify = (text: string, type: "ok" | "error" = "ok") => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 4000);
  };
  const formula = useMemo<ReverseEngineeredFormula | null>(() => contentPerformanceEngine.reverseEngineer(samples), [samples]);
  const calibration = useMemo(() => contentPerformanceEngine.calibrationStats(), [samples]);

  const handleAddSample = () => {
    if (!sampleForm.title.trim()) { notify("请填写标题", "error"); return; }
    if (sampleForm.views <= 0) { notify("播放/阅读量必须为真实数字", "error"); return; }
    contentPerformanceEngine.addSample({
      ...sampleForm,
      tags: tagInput.split(/[,，\s]/).map((t) => t.trim()).filter(Boolean),
      title: sampleForm.title.trim(),
      coverFeatures: sampleForm.coverFeatures.trim(),
      scriptStructure: sampleForm.scriptStructure.trim(),
    });
    refresh();
    setSampleForm(defaultSampleForm());
    setTagInput("");
    notify("✅ 爆款样本已录入（真实数据）");
  };
  const handleRemove = (id: string) => { contentPerformanceEngine.removeSample(id); refresh(); };
  const handleClearAll = () => {
    if (confirm("确认清空全部爆款样本？此操作不可恢复。")) { contentPerformanceEngine.clearSamples(); refresh(); }
  };
  const tabs = [
    { id: "samples" as const, label: "爆款样本", icon: Database },
    { id: "reverse" as const, label: "特征反推", icon: FlaskConical },
    { id: "predict" as const, label: "效果预测", icon: TrendingUp },
    { id: "review" as const, label: "复盘校准", icon: RotateCcw },
  ];

  return (
    <div className="h-full w-full overflow-y-auto">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-pink-500/15 border border-pink-500/30 flex items-center justify-center">
              <TrendingUp size={14} className="text-pink-400" />
            </div>
            <div>
              <h3 className="text-[14px] font-semibold text-white">内容效果预测与爆款反推</h3>
              <p className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">爆款样本 → 反推公式 → 预测 → 发布 → 复盘校准</p>
            </div>
          </div>
          <span className="text-[10px] font-mono text-gray-500">样本 {samples.length} · 已校准 {calibration.count}</span>
        </div>

        <div className="flex items-center gap-1 border-b border-white/[0.06] pb-1">
          {tabs.map((t) => {
            const TIcon = t.icon;
            const active = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-mono uppercase tracking-wider transition-all ${active ? "bg-pink-500/15 text-pink-300 border border-pink-500/30" : "text-gray-500 hover:bg-white/[0.04] border border-transparent"}`}>
                <TIcon size={11} />
                {t.label}
              </button>
            );
          })}
        </div>

        {msg && (
          <div className={`px-3 py-2 rounded-lg border text-[11px] ${msg.type === "ok" ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-300" : "bg-red-500/10 border-red-500/20 text-red-300"}`}>
            {msg.text}
          </div>
        )}

        {tab === "samples" && <SamplesTab samples={samples} form={sampleForm} setForm={setSampleForm} tagInput={tagInput} setTagInput={setTagInput} onAdd={handleAddSample} onRemove={handleRemove} onClear={handleClearAll} />}
        {tab === "reverse" && <ReverseTab formula={formula} />}
        {tab === "predict" && <PredictTab samples={samples} onRefresh={refresh} notify={notify} />}
        {tab === "review" && <ReviewTab samples={samples} onRefresh={refresh} calibration={calibration} notify={notify} />}
      </div>
    </div>
  );
}

// ============================================
// Tab 1：爆款样本录入
// ============================================

function SamplesTab({ samples, form, setForm, tagInput, setTagInput, onAdd, onRemove, onClear }: {
  samples: ViralSample[];
  form: ReturnType<typeof defaultSampleForm>;
  setForm: (f: ReturnType<typeof defaultSampleForm>) => void;
  tagInput: string;
  setTagInput: (v: string) => void;
  onAdd: () => void;
  onRemove: (id: string) => void;
  onClear: () => void;
}) {
  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center gap-1.5 mb-2">
          <Plus size={12} className="text-pink-400" />
          <span className="text-[11px] font-semibold text-white">录入爆款样本（真实数据，来自平台后台/手动记录）</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <label className="text-[9px] font-mono text-gray-500">平台
            <select value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value as PredictPlatform })}
              className="w-full mt-0.5 px-2 py-1 rounded bg-black/30 border border-white/[0.08] text-[11px] text-white">
              {PLATFORMS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </label>
          <label className="text-[9px] font-mono text-gray-500">内容形式
            <select value={form.form} onChange={(e) => setForm({ ...form, form: e.target.value as ContentForm })}
              className="w-full mt-0.5 px-2 py-1 rounded bg-black/30 border border-white/[0.08] text-[11px] text-white">
              {FORMS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
            </select>
          </label>
          <label className="text-[9px] font-mono text-gray-500">发布时间
            <input type="time" value={form.publishTime} onChange={(e) => setForm({ ...form, publishTime: e.target.value })}
              className="w-full mt-0.5 px-2 py-1 rounded bg-black/30 border border-white/[0.08] text-[11px] text-white" />
          </label>
          <label className="text-[9px] font-mono text-gray-500 col-span-3">标题
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="爆款标题"
              className="w-full mt-0.5 px-2 py-1 rounded bg-black/30 border border-white/[0.08] text-[11px] text-white" />
          </label>
          <label className="text-[9px] font-mono text-gray-500">封面特征（逗号分隔）
            <input value={form.coverFeatures} onChange={(e) => setForm({ ...form, coverFeatures: e.target.value })} placeholder="大字报, 真人出镜, 对比图"
              className="w-full mt-0.5 px-2 py-1 rounded bg-black/30 border border-white/[0.08] text-[11px] text-white" />
          </label>
          <label className="text-[9px] font-mono text-gray-500">脚本结构
            <input value={form.scriptStructure} onChange={(e) => setForm({ ...form, scriptStructure: e.target.value })} placeholder="钩子-痛点-方案-行动"
              className="w-full mt-0.5 px-2 py-1 rounded bg-black/30 border border-white/[0.08] text-[11px] text-white" />
          </label>
          <label className="text-[9px] font-mono text-gray-500">标签（逗号分隔）
            <input value={tagInput} onChange={(e) => setTagInput(e.target.value)} placeholder="AI, 企业, 干货"
              className="w-full mt-0.5 px-2 py-1 rounded bg-black/30 border border-white/[0.08] text-[11px] text-white" />
          </label>
          <label className="text-[9px] font-mono text-gray-500">播放/阅读
            <input type="number" min={0} value={form.views} onChange={(e) => setForm({ ...form, views: Number(e.target.value) })}
              className="w-full mt-0.5 px-2 py-1 rounded bg-black/30 border border-white/[0.08] text-[11px] text-white" />
          </label>
          <label className="text-[9px] font-mono text-gray-500">点赞
            <input type="number" min={0} value={form.likes} onChange={(e) => setForm({ ...form, likes: Number(e.target.value) })}
              className="w-full mt-0.5 px-2 py-1 rounded bg-black/30 border border-white/[0.08] text-[11px] text-white" />
          </label>
          <label className="text-[9px] font-mono text-gray-500">评论
            <input type="number" min={0} value={form.comments} onChange={(e) => setForm({ ...form, comments: Number(e.target.value) })}
              className="w-full mt-0.5 px-2 py-1 rounded bg-black/30 border border-white/[0.08] text-[11px] text-white" />
          </label>
          <label className="text-[9px] font-mono text-gray-500">收藏
            <input type="number" min={0} value={form.favorites} onChange={(e) => setForm({ ...form, favorites: Number(e.target.value) })}
              className="w-full mt-0.5 px-2 py-1 rounded bg-black/30 border border-white/[0.08] text-[11px] text-white" />
          </label>
          <label className="text-[9px] font-mono text-gray-500">粉丝增长
            <input type="number" min={0} value={form.followersGain} onChange={(e) => setForm({ ...form, followersGain: Number(e.target.value) })}
              className="w-full mt-0.5 px-2 py-1 rounded bg-black/30 border border-white/[0.08] text-[11px] text-white" />
          </label>
        </div>
        <div className="flex items-center gap-2 mt-2">
          <button onClick={onAdd}
            className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-pink-500/15 border border-pink-500/30 text-[10px] font-mono text-pink-300 hover:bg-pink-500/25 transition-all">
            <Plus size={10} /> 录入样本
          </button>
          {samples.length > 0 && (
            <button onClick={onClear}
              className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-red-500/10 border border-red-500/20 text-[10px] font-mono text-red-300 hover:bg-red-500/20 transition-all">
              <Trash2 size={10} /> 清空
            </button>
          )}
        </div>
      </div>



      {samples.length === 0 ? (
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-4 py-8 text-center text-[11px] text-gray-500">
          <Database size={18} className="inline mr-1 -mt-0.5 text-gray-600" />
          暂无爆款样本。录入 3-5 条真实爆款数据后，系统才能反推公式并预测效果。
        </div>
      ) : (
        <div className="space-y-1.5">
          {samples.map((s) => (
            <div key={s.id} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/[0.05] bg-white/[0.02]">
              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: PLATFORM_COLOR[s.platform] }} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-white truncate">{s.title}</span>
                  <span className="text-[9px] font-mono text-gray-500">{PLATFORMS.find((p) => p.id === s.platform)?.label}</span>
                </div>
                <div className="text-[9px] font-mono text-gray-500">
                  播放 {fmt(s.views)} · 赞 {fmt(s.likes)} · 评 {fmt(s.comments)} · 藏 {fmt(s.favorites)} · 涨粉 {fmt(s.followersGain)} · {s.publishTime}
                </div>
              </div>
              <button onClick={() => onRemove(s.id)} className="text-gray-600 hover:text-red-400 transition-colors">
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// Tab 2：特征反推（从爆款样本提炼公式）
// ============================================

function ReverseTab({ formula }: { formula: ReverseEngineeredFormula | null }) {
  if (!formula) {
    return (
      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-4 py-10 text-center text-[11px] text-gray-500">
        <FlaskConical size={18} className="inline mr-1 -mt-0.5 text-gray-600" />
        暂无爆款样本，无法反推公式。请先在「爆款样本」录入真实数据。
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 gap-3">
      <FormulaCard icon={<Type size={12} />} title="标题公式" color="#F472B6">
        <p className="text-[12px] text-white font-mono">{formula.titleFormula}</p>
        <p className="text-[9px] font-mono text-gray-500 mt-1">基于 {formula.sampleSize} 条爆款样本统计</p>
      </FormulaCard>
      <FormulaCard icon={<ImageIcon size={12} />} title="高频封面特征" color="#38BDF8">
        <div className="flex flex-wrap gap-1">
          {formula.topCoverFeatures.map((f) => (
            <span key={f.feature} className="px-1.5 py-0.5 rounded bg-white/[0.03] border border-white/[0.06] text-[9px] font-mono text-gray-300">
              {f.feature} ×{f.count}
            </span>
          ))}
        </div>
      </FormulaCard>
      <FormulaCard icon={<Layers size={12} />} title="高频脚本结构" color="#A78BFA">
        <div className="flex flex-wrap gap-1">
          {formula.topScriptStructures.map((s) => (
            <span key={s.structure} className="px-1.5 py-0.5 rounded bg-white/[0.03] border border-white/[0.06] text-[9px] font-mono text-gray-300">
              {s.structure} ×{s.count}
            </span>
          ))}
        </div>
      </FormulaCard>
      <FormulaCard icon={<Hash size={12} />} title="高频标签" color="#34D399">
        <div className="flex flex-wrap gap-1">
          {formula.topTags.map((t) => (
            <span key={t.tag} className="px-1.5 py-0.5 rounded bg-white/[0.03] border border-white/[0.06] text-[9px] font-mono text-gray-300">
              #{t.tag} ×{t.count}
            </span>
          ))}
        </div>
      </FormulaCard>
      <FormulaCard icon={<Clock size={12} />} title="最佳发布时间段" color="#FBBF24" wide>
        <div className="flex gap-2">
          {formula.bestTimeSlots.map((s) => (
            <span key={s.slot} className="px-2 py-1 rounded bg-white/[0.03] border border-white/[0.06] text-[10px] font-mono text-gray-300">
              {s.slot} · 均播放 {fmt(s.avgViews)} · {s.count} 条
            </span>
          ))}
        </div>
      </FormulaCard>
    </div>
  );
}

function FormulaCard({ icon, title, color, children, wide }: {
  icon: React.ReactNode; title: string; color: string; children: React.ReactNode; wide?: boolean;
}) {
  return (
    <div className={`rounded-lg border border-white/[0.06] bg-white/[0.02] p-3 ${wide ? "col-span-2" : ""}`}>
      <div className="flex items-center gap-1.5 mb-2" style={{ color }}>
        {icon}
        <span className="text-[10px] font-mono uppercase tracking-wider">{title}</span>
      </div>
      {children}
    </div>
  );
}

// ============================================
// Tab 3：效果预测
// ============================================

function PredictTab({ samples, onRefresh, notify }: {
  samples: ViralSample[];
  onRefresh: () => void;
  notify: (t: string, type?: "ok" | "error") => void;
}) {
  const [input, setInput] = useState<PredictInput>(defaultPredictInput());
  const [tagInput, setTagInput] = useState("");
  const [prediction, setPrediction] = useState<ContentPrediction | null>(null);

  const runPredict = () => {
    if (!input.title.trim()) { notify("请填写要预测的内容标题", "error"); return; }
    const result = contentPerformanceEngine.predict({
      ...input,
      tags: tagInput.split(/[,，\s]/).map((t) => t.trim()).filter(Boolean),
      title: input.title.trim(),
      coverFeatures: input.coverFeatures.trim(),
      scriptStructure: input.scriptStructure.trim(),
    });
    setPrediction(result);
    notify(result.sampleSize > 0 ? `预测完成（基于 ${result.sampleSize} 条样本，置信度 ${result.confidence}%）` : "无同平台样本，返回平台基线（非预测）");
  };

  const saveAsSample = () => {
    if (!prediction) return;
    contentPerformanceEngine.addSample({
      platform: input.platform,
      title: input.title,
      coverFeatures: input.coverFeatures,
      scriptStructure: input.scriptStructure,
      tags: tagInput.split(/[,，\s]/).map((t) => t.trim()).filter(Boolean),
      form: input.form,
      views: 0, likes: 0, comments: 0, favorites: 0, followersGain: 0,
      publishTime: input.publishTime,
    });
    const list = contentPerformanceEngine.listSamples();
    const last = list[list.length - 1];
    if (last) contentPerformanceEngine.attachPrediction(last.id, prediction);
    onRefresh();
    notify("已保存为待发布样本（含预测），发布后到「复盘校准」回填实际数据");
  };

  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center gap-1.5 mb-2">
          <Target size={12} className="text-pink-400" />
          <span className="text-[11px] font-semibold text-white">预测新内容效果</span>
        </div>
        <div className="space-y-2">
          <label className="text-[9px] font-mono text-gray-500 block">平台
            <select value={input.platform} onChange={(e) => setInput({ ...input, platform: e.target.value as PredictPlatform })}
              className="w-full mt-0.5 px-2 py-1 rounded bg-black/30 border border-white/[0.08] text-[11px] text-white">
              {PLATFORMS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </label>
          <label className="text-[9px] font-mono text-gray-500 block">标题
            <input value={input.title} onChange={(e) => setInput({ ...input, title: e.target.value })} placeholder="要发布的内容标题"
              className="w-full mt-0.5 px-2 py-1 rounded bg-black/30 border border-white/[0.08] text-[11px] text-white" />
          </label>
          <label className="text-[9px] font-mono text-gray-500 block">封面特征
            <input value={input.coverFeatures} onChange={(e) => setInput({ ...input, coverFeatures: e.target.value })} placeholder="大字报, 真人出镜"
              className="w-full mt-0.5 px-2 py-1 rounded bg-black/30 border border-white/[0.08] text-[11px] text-white" />
          </label>
          <label className="text-[9px] font-mono text-gray-500 block">脚本结构
            <input value={input.scriptStructure} onChange={(e) => setInput({ ...input, scriptStructure: e.target.value })} placeholder="钩子-痛点-方案-行动"
              className="w-full mt-0.5 px-2 py-1 rounded bg-black/30 border border-white/[0.08] text-[11px] text-white" />
          </label>
          <label className="text-[9px] font-mono text-gray-500 block">标签
            <input value={tagInput} onChange={(e) => setTagInput(e.target.value)} placeholder="AI, 企业"
              className="w-full mt-0.5 px-2 py-1 rounded bg-black/30 border border-white/[0.08] text-[11px] text-white" />
          </label>
          <label className="text-[9px] font-mono text-gray-500 block">发布时间
            <input type="time" value={input.publishTime} onChange={(e) => setInput({ ...input, publishTime: e.target.value })}
              className="w-full mt-0.5 px-2 py-1 rounded bg-black/30 border border-white/[0.08] text-[11px] text-white" />
          </label>
          <button onClick={runPredict}
            className="w-full py-2 rounded-md bg-pink-500/15 border border-pink-500/30 text-[11px] font-mono text-pink-300 hover:bg-pink-500/25 transition-all">
            <TrendingUp size={11} className="inline mr-1 -mt-0.5" /> 预测效果
          </button>
        </div>
      </div>
      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <BarChart3 size={12} className="text-emerald-400" />
            <span className="text-[11px] font-semibold text-white">预测结果</span>
          </div>
          {prediction && (
            <span className="text-[9px] font-mono text-gray-500">置信度 {prediction.confidence}% · {prediction.sampleSize} 样本</span>
          )}
        </div>
        {!prediction ? (
          <div className="text-center py-10 text-[11px] text-gray-500">
            填写左侧内容特征后点击「预测效果」。
            <div className="text-[9px] text-gray-600 mt-1">预测播放/点赞/评论/收藏/粉丝增长区间 + 爆款命中概率</div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-mono text-gray-500">爆款命中概率</span>
              <span className={`text-[14px] font-mono font-semibold ${prediction.hitProbability >= 50 ? "text-emerald-400" : "text-amber-400"}`}>
                {prediction.hitProbability}%
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${prediction.hitProbability}%`, background: prediction.hitProbability >= 50 ? "#34D399" : "#FBBF24" }} />
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <PredRow label="播放/阅读" pred={prediction.views} />
              <PredRow label="点赞" pred={prediction.likes} />
              <PredRow label="评论" pred={prediction.comments} />
              <PredRow label="收藏" pred={prediction.favorites} />
              <PredRow label="粉丝增长" pred={prediction.followersGain} />
            </div>
            <div className="text-[9px] font-mono text-gray-500 mt-2">
              {prediction.basis.map((b, i) => <div key={i}>· {b}</div>)}
            </div>
            <div className="px-2 py-1.5 rounded bg-white/[0.03] border border-white/[0.06] text-[10px] text-gray-300">
              💡 {prediction.recommendation}
            </div>
            <button onClick={saveAsSample}
              className="w-full py-1.5 rounded-md bg-emerald-500/10 border border-emerald-500/20 text-[10px] font-mono text-emerald-300 hover:bg-emerald-500/20 transition-all">
              <CheckCircle2 size={10} className="inline mr-1 -mt-0.5" /> 保存为样本（发布后回填校准）
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function PredRow({ label, pred }: { label: string; pred: { low: number; mid: number; high: number } }) {
  return (
    <div className="rounded border border-white/[0.05] bg-white/[0.02] px-2 py-1.5">
      <div className="text-[9px] font-mono text-gray-500">{label}</div>
      <div className="text-[11px] font-mono text-white">{fmt(pred.mid)} <span className="text-[9px] text-gray-500">({fmt(pred.low)}~{fmt(pred.high)})</span></div>
    </div>
  );
}

// ============================================
// Tab 4：复盘校准（发布后回填实际数据）
// ============================================

function ReviewTab({ samples, onRefresh, calibration, notify }: {
  samples: ViralSample[];
  onRefresh: () => void;
  calibration: { count: number; errorByMetric: Record<string, number> };
  notify: (t: string, type?: "ok" | "error") => void;
}) {
  const [actuals, setActuals] = useState<Record<string, { views: number; likes: number; comments: number; favorites: number; followersGain: number }>>({});

  const pending = samples.filter((s) => s.predicted);
  const metricLabels: Record<string, string> = { views: "播放", likes: "点赞", comments: "评论", favorites: "收藏", followersGain: "粉丝" };

  const handleRecord = (id: string) => {
    const a = actuals[id];
    if (!a) { notify("请先填写实际数据", "error"); return; }
    if (a.views <= 0) { notify("实际播放/阅读量必须 > 0", "error"); return; }
    contentPerformanceEngine.recordActual(id, a);
    onRefresh();
    setActuals((prev) => { const n = { ...prev }; delete n[id]; return n; });
    notify("✅ 已回填实际数据并校准预测模型");
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-6 gap-2">
        {Object.entries(metricLabels).map(([k, label]) => {
          const err = calibration.errorByMetric[`error_${k}`];
          return (
            <div key={k} className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-2 py-2 text-center">
              <div className="text-[9px] font-mono text-gray-500">{label}偏差</div>
              <div className={`text-[13px] font-mono font-semibold ${err === undefined ? "text-gray-600" : err <= 0.4 ? "text-emerald-400" : err <= 0.7 ? "text-amber-400" : "text-red-400"}`}>
                {err === undefined ? "—" : `${Math.round(err * 100)}%`}
              </div>
            </div>
          );
        })}
      </div>
      <div className="text-[9px] font-mono text-gray-500">
        已校准 {calibration.count} 条 · 偏差越低预测越准，系统随回填自动优化
      </div>

      {pending.length === 0 ? (
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-4 py-8 text-center text-[11px] text-gray-500">
          <RotateCcw size={18} className="inline mr-1 -mt-0.5 text-gray-600" />
          暂无待校准内容。先在「效果预测」保存含预测的样本，发布后回来回填实际数据。
        </div>
      ) : (
        <div className="space-y-1.5">
          {pending.map((s) => (
            <div key={s.id} className="rounded-lg border border-white/[0.05] bg-white/[0.02] p-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-white truncate">{s.title}</span>
                <span className="text-[9px] font-mono text-gray-500">预测 {fmt(s.predicted!.views)} 播放</span>
              </div>
              <div className="grid grid-cols-5 gap-1.5 mt-1.5">
                <ActualInput label="播放" value={actuals[s.id]?.views} onChange={(v) => setActuals({ ...actuals, [s.id]: { ...(actuals[s.id] || { views: 0, likes: 0, comments: 0, favorites: 0, followersGain: 0 }), views: v } })} />
                <ActualInput label="点赞" value={actuals[s.id]?.likes} onChange={(v) => setActuals({ ...actuals, [s.id]: { ...(actuals[s.id] || { views: 0, likes: 0, comments: 0, favorites: 0, followersGain: 0 }), likes: v } })} />
                <ActualInput label="评论" value={actuals[s.id]?.comments} onChange={(v) => setActuals({ ...actuals, [s.id]: { ...(actuals[s.id] || { views: 0, likes: 0, comments: 0, favorites: 0, followersGain: 0 }), comments: v } })} />
                <ActualInput label="收藏" value={actuals[s.id]?.favorites} onChange={(v) => setActuals({ ...actuals, [s.id]: { ...(actuals[s.id] || { views: 0, likes: 0, comments: 0, favorites: 0, followersGain: 0 }), favorites: v } })} />
                <ActualInput label="粉丝" value={actuals[s.id]?.followersGain} onChange={(v) => setActuals({ ...actuals, [s.id]: { ...(actuals[s.id] || { views: 0, likes: 0, comments: 0, favorites: 0, followersGain: 0 }), followersGain: v } })} />
              </div>
              <div className="flex justify-end mt-1.5">
                <button onClick={() => handleRecord(s.id)}
                  className="px-2.5 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/20 text-[10px] font-mono text-emerald-300 hover:bg-emerald-500/20 transition-all">
                  <CheckCircle2 size={10} className="inline mr-1 -mt-0.5" /> 回填并校准
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ActualInput({ label, value, onChange }: { label: string; value?: number; onChange: (v: number) => void }) {
  return (
    <label className="text-[9px] font-mono text-gray-500 block">
      {label}
      <input type="number" min={0} value={value ?? ""} placeholder="0"
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full mt-0.5 px-1.5 py-0.5 rounded bg-black/30 border border-white/[0.08] text-[10px] font-mono text-white" />
    </label>
  );
}

